import React from 'react';
import { X, Check, RefreshCw } from 'lucide-react';
import { GiftRecord, GiftType, RSVPResponse } from '../../../types/invitation';
import ThemedCard from '../../themed/ThemedCard';
import { GiftFormData } from '../types';

interface AddEditFormTabProps {
  editingRecord: GiftRecord | null;
  formData: GiftFormData;
  setFormData: React.Dispatch<React.SetStateAction<GiftFormData>>;
  formErrors: Record<string, string>;
  saving: boolean;
  rsvpResponses: RSVPResponse[];
  onSubmit: (e: React.FormEvent) => Promise<void>;
  onCancel: () => void;
  onFillFromRSVP: (rsvp: RSVPResponse) => void;
}

export const AddEditFormTab: React.FC<AddEditFormTabProps> = ({
  editingRecord,
  formData,
  setFormData,
  formErrors,
  saving,
  rsvpResponses,
  onSubmit,
  onCancel,
  onFillFromRSVP,
}) => {
  return (
    <ThemedCard className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-wedding-text-title">
          {editingRecord ? '編輯紅包記錄' : '新增紅包記錄'}
        </h2>
        {editingRecord && (
          <button
            onClick={onCancel}
            className="p-2 text-wedding-text-body hover:bg-wedding-pink/10 rounded"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {!editingRecord && rsvpResponses.length > 0 && (
        <div className="mb-6 p-4 bg-wedding-pink/5 rounded-lg">
          <h3 className="text-sm font-medium text-wedding-text-title mb-3">從出席賓客快速填入：</h3>
          <div className="flex flex-wrap gap-2">
            {rsvpResponses.slice(0, 10).map((rsvp) => (
              <button
                key={rsvp.id}
                onClick={() => onFillFromRSVP(rsvp)}
                className="px-3 py-1 text-sm bg-white border border-wedding-pink/30 rounded-full hover:bg-wedding-pink/10 transition-colors"
              >
                {rsvp.guestName}
              </button>
            ))}
            {rsvpResponses.length > 10 && (
              <span className="px-3 py-1 text-sm text-wedding-text-body">
                +{rsvpResponses.length - 10} 更多...
              </span>
            )}
          </div>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-wedding-text-title mb-2">
              賓客姓名 *
            </label>
            <input
              type="text"
              value={formData.guestName}
              onChange={(e) => setFormData(prev => ({ ...prev, guestName: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink ${
                formErrors.guestName ? 'border-red-500' : 'border-wedding-pink/30'
              }`}
              placeholder="請輸入賓客姓名"
            />
            {formErrors.guestName && (
              <p className="mt-1 text-sm text-red-500">{formErrors.guestName}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-wedding-text-title mb-2">
              電子郵件
            </label>
            <input
              type="email"
              value={formData.guestEmail}
              onChange={(e) => setFormData(prev => ({ ...prev, guestEmail: e.target.value }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink ${
                formErrors.guestEmail ? 'border-red-500' : 'border-wedding-pink/30'
              }`}
              placeholder="請輸入電子郵件（選填）"
            />
            {formErrors.guestEmail && (
              <p className="mt-1 text-sm text-red-500">{formErrors.guestEmail}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-wedding-text-title mb-2">
              金額 *
            </label>
            <input
              type="number"
              min="0"
              step="1"
              value={formData.amount || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, amount: Number(e.target.value) }))}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink ${
                formErrors.amount ? 'border-red-500' : 'border-wedding-pink/30'
              }`}
              placeholder="請輸入金額"
            />
            {formErrors.amount && (
              <p className="mt-1 text-sm text-red-500">{formErrors.amount}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-wedding-text-title mb-2">
              幣別
            </label>
            <select
              value={formData.currency}
              onChange={(e) => setFormData(prev => ({ ...prev, currency: e.target.value }))}
              className="w-full px-3 py-2 border border-wedding-pink/30 rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink"
            >
              <option value="TWD">台幣 (TWD)</option>
              <option value="USD">美金 (USD)</option>
              <option value="CNY">人民幣 (CNY)</option>
              <option value="HKD">港幣 (HKD)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-wedding-text-title mb-2">
              紅包類型
            </label>
            <select
              value={formData.giftType}
              onChange={(e) => setFormData(prev => ({ ...prev, giftType: e.target.value as GiftType }))}
              className="w-full px-3 py-2 border border-wedding-pink/30 rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink"
            >
              <option value="cash">現金</option>
              <option value="gift">禮品</option>
              <option value="both">現金+禮品</option>
            </select>
          </div>
        </div>

        {(formData.giftType === 'gift' || formData.giftType === 'both') && (
          <div>
            <label className="block text-sm font-medium text-wedding-text-title mb-2">
              禮品描述
            </label>
            <input
              type="text"
              value={formData.giftDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, giftDescription: e.target.value }))}
              className="w-full px-3 py-2 border border-wedding-pink/30 rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink"
              placeholder="請描述禮品內容"
            />
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-wedding-text-title mb-2">
            留言
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-wedding-pink/30 rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink"
            placeholder="賓客留言或其他備註（選填）"
          />
        </div>

        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-wedding-pink/30 text-wedding-text-body rounded-lg hover:bg-wedding-pink/5 transition-colors"
          >
            取消
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center px-6 py-2 bg-wedding-pink text-white rounded-lg hover:bg-wedding-pink/90 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            {editingRecord ? '更新記錄' : '新增記錄'}
          </button>
        </div>
      </form>
    </ThemedCard>
  );
};
